<template>
  <fieldset
    id="payment-history"
    class="account__form-section account__form-section--wide"
  >
    <legend class="account__form-section-title">{{ formSection }}</legend>
    <div class="table">
      <div class="layout--text-button-wrap">
        <div class="input-group__field-wrap">
          <label for="search" class="input-group__label">Search</label>
          <input id="search" class="input-group__input" v-model="search" />
        </div>
        <div
          class="input-group__field-wrap layout--flex-align-center layout--flex-end table__search-wrap"
        >
          <label for="resultsPerPage">Results per page:&nbsp;</label>
          <div class="input-group__select-wrap input-group__select-wrap--small">
            <select
              id="resultsPerPage"
              class="input-group__select weight--bold"
              v-model="resultsPerPage"
            >
              <option
                v-for="option in resultsPerPageOptions"
                :key="option.value"
                :value="option.value"
                >{{ option.label }}</option
              >
            </select>
          </div>
        </div>
      </div>
      <table class="table">
        <caption class="layout--screen-reader-only">
          {{
            formSection
          }}
        </caption>
        <thead>
          <tr>
            <th
              id="type"
              class="table__header table__header--squish table__cell"
              scope="col"
            >
              <button
                type="button"
                class="table__sort-button"
                :class="{
                  'table__sort-button--descending':
                    (sort.direction === 'descending') & (sort.field === 'type'),
                  'table__sort-button--ascending':
                    (sort.direction === 'ascending') & (sort.field === 'type'),
                }"
                @click="toggleSort('type')"
              >
                Type
              </button>
            </th>
            <th
              id="amount"
              class="table__header table__header--squish table__cell"
              scope="col"
            >
              <button
                type="button"
                class="table__sort-button"
                :class="{
                  'table__sort-button--descending':
                    (sort.direction === 'descending') &
                    (sort.field === 'amount'),
                  'table__sort-button--ascending':
                    (sort.direction === 'ascending') &
                    (sort.field === 'amount'),
                }"
                @click="toggleSort('amount')"
              >
                Amount
              </button>
            </th>
            <th
              id="date"
              class="table__header table__header--squish table__cell"
              scope="col"
            >
              <button
                type="button"
                class="table__sort-button"
                :class="{
                  'table__sort-button--descending':
                    (sort.direction === 'descending') & (sort.field === 'date'),
                  'table__sort-button--ascending':
                    (sort.direction === 'ascending') & (sort.field === 'date'),
                }"
                @click="toggleSort('date')"
              >
                Date
              </button>
            </th>
            <th
              id="memo"
              class="table__header table__header--squish table__cell"
              scope="col"
            >
              <button
                type="button"
                class="table__sort-button"
                :class="{
                  'table__sort-button--descending':
                    (sort.direction === 'descending') & (sort.field === 'memo'),
                  'table__sort-button--ascending':
                    (sort.direction === 'ascending') & (sort.field === 'memo'),
                }"
                @click="toggleSort('memo')"
              >
                Memo
              </button>
            </th>
            <th
              id="print"
              class="table__header table__header--squish layout--screen-reader-only"
              scope="col"
            >
              Print
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="receipt in receipts"
            :key="receipt.id"
            class="table__row table__row--sqish"
          >
            <td
              class="table__cell table__cell--squish table__cell--squish-title table__cell--auto"
            >
              {{
                getTransactionTypeDescription(receipt.transactionType)
                  | capitalizeFirstLetter
              }}
            </td>
            <td class="table__cell table__cell--squish table__cell--auto">
              {{ receipt.transactionAmount | currency }}
            </td>
            <td class="table__cell table__cell--squish table__cell--auto">
              {{ receipt.createdAt | date }}
            </td>
            <td class="table__cell table__cell--squish table__cell--auto">
              {{ receipt.memo }}
            </td>
            <td class="table__cell table__cell--squish table__cell--auto">
              <router-link
                class="link layout--text-left"
                target="_blank"
                :to="{ name: 'payment receipt', query: { id: receipt.id } }"
                >Print</router-link
              >
            </td>
          </tr>
        </tbody>
      </table>
      <nav aria-label="Payment history table controls">
        <ul
          class="dashboard__table-nav dashboard__table-nav--paginate layout--flex-wrap"
        >
          <li
            class="dashboard__table-nav-item dashboard__table-nav-item--showing"
          >
            Showing {{ firstReceiptOnPage }} to {{ lastReceiptOnPage }} of
            {{ searchFilterReceipts.length }}
          </li>
          <li class="dashboard__table-nav-item">
            <button
              class="button button--secondary"
              type="button"
              @click="previousPage"
            >
              Previous
            </button>
          </li>
          <li class="dashboard__table-nav-item">
            <button
              class="button button--secondary"
              type="button"
              @click="nextPage"
            >
              Next
            </button>
          </li>
        </ul>
      </nav>
    </div>
  </fieldset>
</template>

<script>
import { mapGetters } from "vuex";

import {
  getBillingTransactionTypeDescription,
  timestampToDateString,
  timestampToDateObject,
} from "../../../helpers/helpers";

const formSection = "Payment History";

export default {
  name: "PaymentHistory",
  data() {
    return {
      formSection,
      search: "",
      resultsPerPage: 5,
      currentPage: 1,
      resultsPerPageOptions: [
        { value: 5, label: "5" },
        { value: 10, label: "10" },
        { value: 20, label: "20" },
        { value: 0, label: "All" },
      ],
      sort: {
        direction: "descending",
        field: "date",
      },
    };
  },
  computed: {
    ...mapGetters("account", ["paymentReceipts"]),
    // return timers list filtered by the user's search term based on widget name
    searchFilterReceipts: function() {
      if (this.search !== "") {
        return this.paymentReceipts.filter(r => {
          if (
            getBillingTransactionTypeDescription(r.transactionType)
              .toLowerCase()
              .includes(this.search.toLowerCase())
          ) {
            return true;
          }
          if (
            r.transactionAmount.toString().includes(this.search.toLowerCase())
          ) {
            return true;
          }
          if (timestampToDateString(r.createdAt).includes(this.search)) {
            return true;
          }
          if (r.memo.toLowerCase().includes(this.search.toLowerCase())) {
            return true;
          }
          return false;
        });
      }
      return this.paymentReceipts;
    },
    sortFilteredReceipts: function() {
      const newReceipts = [...this.searchFilterReceipts];
      if (this.sort.field === "date") {
        if (this.sort.direction === "ascending") {
          return newReceipts.sort((a, b) => {
            const aDate = timestampToDateObject(a.createdAt);
            const bDate = timestampToDateObject(b.createdAt);
            return aDate - bDate;
          });
        }
        return newReceipts.sort((a, b) => {
          const aDate = timestampToDateObject(a.createdAt);
          const bDate = timestampToDateObject(b.createdAt);
          return bDate - aDate;
        });
      }
      if (this.sort.field === "amount") {
        if (this.sort.direction === "ascending") {
          return newReceipts.sort((a, b) => {
            return a.transactionAmount - b.transactionAmount;
          });
        }
        return newReceipts.sort((a, b) => {
          return b.transactionAmount - a.transactionAmount;
        });
      }
      if (this.sort.field === "type") {
        if (this.sort.direction === "ascending") {
          return newReceipts.sort((a, b) => {
            const aType = getBillingTransactionTypeDescription(
              a.transactionType
            );
            const bType = getBillingTransactionTypeDescription(
              b.transactionType
            );
            if (aType < bType) {
              return -1;
            }
            if (aType > bType) {
              return 1;
            }
            return 0;
          });
        }
        return newReceipts.sort((a, b) => {
          const aType = getBillingTransactionTypeDescription(a.transactionType);
          const bType = getBillingTransactionTypeDescription(b.transactionType);
          if (aType > bType) {
            return -1;
          }
          if (aType < bType) {
            return 1;
          }
          return 0;
        });
      }
      if (this.sort.field === "memo") {
        if (this.sort.direction === "ascending") {
          return newReceipts.sort((a, b) => {
            const aMemo = a.memo.toLowerCase();
            const bMemo = b.memo.toLowerCase();
            if (aMemo < bMemo) {
              return -1;
            }
            if (aMemo > bMemo) {
              return 1;
            }
            return 0;
          });
        }
        return newReceipts.sort((a, b) => {
          const aMemo = a.memo.toLowerCase();
          const bMemo = b.memo.toLowerCase();
          if (aMemo > bMemo) {
            return -1;
          }
          if (aMemo < bMemo) {
            return 1;
          }
          return 0;
        });
      }
      return newReceipts;
    },
    receipts: function() {
      if (this.resultsPerPage > 0) {
        return this.sortFilteredReceipts.slice(
          (this.currentPage - 1) * this.resultsPerPage,
          (this.currentPage - 1) * this.resultsPerPage + this.resultsPerPage
        );
      }
      return this.sortFilteredReceipts;
    },
    numberOfPages: function() {
      return Math.ceil(this.searchFilterReceipts.length / this.resultsPerPage);
    },
    // the number for the first receipt on the page being viewed
    firstReceiptOnPage: function() {
      const firstReceipt = (this.currentPage - 1) * this.resultsPerPage + 1;
      if (firstReceipt < this.sortFilteredReceipts.length) {
        return firstReceipt;
      }
      return this.sortFilteredReceipts.length;
    },
    // number for the last receipt on the page being viewed
    lastReceiptOnPage: function() {
      if (this.resultsPerPage > 0) {
        const lastReceipt =
          (this.currentPage - 1) * this.resultsPerPage + this.resultsPerPage;
        if (lastReceipt < this.sortFilteredReceipts.length) {
          return lastReceipt;
        }
        return this.sortFilteredReceipts.length;
      }
      return this.sortFilteredReceipts.length;
    },
  },
  methods: {
    getTransactionTypeDescription: function(transactionType) {
      return getBillingTransactionTypeDescription(transactionType);
    },
    setCurrentPage: function(newPage) {
      this.currentPage = newPage;
    },
    nextPage: function() {
      if (this.currentPage < this.numberOfPages) {
        this.currentPage = this.currentPage + 1;
      }
    },
    previousPage: function() {
      if (this.currentPage > 1) {
        this.currentPage = this.currentPage - 1;
      }
    },
    toggleSort: function(field) {
      if (this.sort.field === field) {
        this.toggleSortDirection();
      } else {
        this.sort.field = field;
        this.sort.direction = "descending";
      }
    },
    toggleSortDirection: function() {
      if (this.sort.direction === "descending") {
        this.sort.direction = "ascending";
      } else {
        this.sort.direction = "descending";
      }
    },
  },
};
</script>

<style></style>
